import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Dropzone } from './dropzone'
import Resx from '@/components/Resx'
import { actionCreators } from '@/store/FileService'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { dialogTypes } from '@/dialogs'
import { userError, serverError } from '@/store/Errors'
import { FileItem, humanFileSize } from './fileItem'
import uuid from 'uuid'
import moment from 'moment'
import { format } from '@/utils/StringUtilities'
import { localized } from '@/decorators/localized'

const MAX_UPLOADED_FILE_SIZE = 100 * 1024 * 1024

const formattedFileSize = (length) => {
  const f = humanFileSize(length)
  return `${f.size} ${f.unit}`
}

function mapStateToProps (state, ownProps) {
  let fileStates = {}
  ownProps.attachments && ownProps.attachments.forEach(a => {
    const queuePosition = state.fileService.queue.indexOf(a.id)
    const file = state.fileService.files[a.id]
    let status = null
    if (queuePosition === 0) status = state.fileService.uploadProgress // upload in progress
    else if (queuePosition > 0) status = 'pending' // upload not started
    else if (queuePosition < 0) {
      if (file) {
        status = file.error || 'Common.FileUploadFailed'
      } else {
        status = 'existing'
      }
    }
    fileStates[a.id] = status
  })
  return {
    ...ownProps,
    fileStates
  }
}

const mapDispatchToProps = {
  upload: actionCreators.upload,
  cancel: actionCreators.cancel,
  remove: actionCreators.remove,
  userError,
  serverError,
  openDialog: dialogActions.openDialog
}

const operations = {
  add: 'add',
  remove: 'remove',
  rename: 'rename'
}

function defaultChangeHandler ({ file, operation }, attachments) {
  switch (operation) {
    case operations.add:
      return [...attachments.filter(x => x.id !== file.id), file]
    case operations.remove:
      return [...attachments.filter(x => x.id !== file.id)]
    case operations.rename:
      return attachments.map(x => x.id === file.id ? { ...x, name: file.name } : x)
    default:
      return attachments
  }
}

@localized()
@connect(mapStateToProps, mapDispatchToProps)
class FileAttachments extends React.Component {
  static propTypes = {
    attachments: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string, // UUID
      name: PropTypes.string,
      length: PropTypes.number,
      verified: PropTypes.bool
    })).isRequired,
    downloadAction: PropTypes.func,
    onChange: PropTypes.func,
    onFilesAdded: PropTypes.func, // this is dispatched when files are saved in the database (however not uploaded yet)
    singleFile: PropTypes.bool,
    uploadButtonText: PropTypes.string,
    readonly: PropTypes.bool,
    isRemoveEnabled: PropTypes.bool,
    customActions: PropTypes.arrayOf(PropTypes.shape({
      buttonContent: PropTypes.object.isRequired,
      buttonProps: PropTypes.object,
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.bool
    })),
    accept: PropTypes.string,

    upload: PropTypes.func,
    cancel: PropTypes.func,
    remove: PropTypes.func,
    fileStates: PropTypes.array
  }
  static defaultProps = {
    isRemoveEnabled: true
  }

  state = {
    dropzoneActive: false
  }

  onChange (file, operation) {
    const { onChange, readonly } = this.props
    if (typeof onChange === 'function' && !readonly) {
      this.props.onChange({
        operation,
        file
      })
    }
  }

  onRemove = (uuid) => {
    if (this.props.isRemoveEnabled) {
      const { remove, cancel, attachments, fileStates } = this.props
      const file = attachments.find(x => x.id === uuid)
      if (file) {
        this.onChange(file, operations.remove)
        const fileState = fileStates[file.id]
        if (typeof fileState === 'number') {
          cancel(uuid)
          return
        }
      }
      remove(uuid)
    }
  }

  onRename = (uuid, name) => {
    const { attachments } = this.props
    const file = attachments.find(x => x.id === uuid)
    if (file) {
      this.onChange({ ...file, name }, operations.rename)
    }
  }

  onDrop = (files) => {
    const largeFiles = files.filter(f => f.size > MAX_UPLOADED_FILE_SIZE)
    if (largeFiles.length === 0) {
      const { singleFile, attachments, upload } = this.props
      if (singleFile && attachments.length > 0) {
        attachments.forEach(a => this.onChange(a, operations.remove))
      }
      for (let i = 0; i < files.length; i++) {
        let f = files[i]
        f.id = uuid()
        this.onChange({ id: f.id, name: f.name, length: f.size, type: f.type, creationTime: moment.utc().toDate() }, operations.add)
        if (singleFile) break
      }
      const callback = typeof this.props.onFilesAdded === 'function'
        ? () => this.props.onFilesAdded(files)
        : () => {}
      upload(files, callback)
    } else {
      const filesInfo = largeFiles.map(f => `${f.name} (${formattedFileSize(f.size)})`).join(', ')
      const message = format(this.props.localize('Common.MaxUploadFileSize'), formattedFileSize(MAX_UPLOADED_FILE_SIZE), filesInfo)
      this.props.openDialog({ dialogType: dialogTypes.alert, localizedContent: message })
    }
  }

  render () {
    const { readonly, uploadButtonText, dropzoneFullWidth } = this.props

    if (readonly) {
      return this.renderFiles()
    }
    return (
      <React.Fragment>
        <div>
          {this.renderFiles()}
          <Dropzone
            uploadButtonText={uploadButtonText || 'Common.DragAndDropFile'}
            accept={this.props.accept}
            disableClick
            type='files'
            style={{}}
            onDrop={this.onDrop}
            fullWidth={dropzoneFullWidth}
          />
        </div>
      </React.Fragment>
    )
  }

  renderFiles () {
    const { attachments, fileStates, readonly, downloadAction, customActions, isRemoveEnabled } = this.props
    if (!attachments || !attachments.length) {
      return readonly ? <Resx name='Common.NoAttachments' /> : null
    }
    return (
      <ul className='file-attachments'>
        {
          attachments.map((file, i) =>
            <FileItem {...file} key={i} state={fileStates[file.id]} readonly={readonly}
              onRemove={this.onRemove} onRename={this.onRename} downloadAction={downloadAction}
              customActions={customActions} isRemoveEnabled={isRemoveEnabled} />)
        }
      </ul>
    )
  }
}

export {
  FileAttachments,
  operations,
  defaultChangeHandler
}



// WEBPACK FOOTER //
// ./src/components/fileAttachments/fileAttachments.js